import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/legacy-form-field';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

import { provideTransloco, TranslocoPipe } from '@jsverse/transloco';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsModule } from '@ngxs/store';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { LatinisePipe, SlugifyPipe, StripTagsPipe } from 'ngx-pipes';

import * as AnalyticsApiModule from '@analytics-api/api.module';

import * as AuditLogApiModule from '@audit-log/api.module';

import * as Auth0ApiModule from '@auth0-api/api.module';

import * as ClientStatisticsApiModule from '@client-statistics-api/api.module';

import * as EsgApiModule from '@esg-api/api.module';

import * as LinkShortenerApiModule from '@link-shortener-api/api.module';

import * as MailApiModule from '@mail-api/api.module';

import * as PdfGeneratorApiModule from '@pdf-generator-api/api.module';

import * as PurchaseApiModule from '@purchase-api/api.module';

import * as SmartHeadApiModule from '@smarthead-api/api.module';

import * as UserRankApiModule from '@user-rank-api/api.module';

import { ANGULAR_LOCALE_SERVICE_TOKEN, UiPlatformNotificationComponent } from 'ui-platform';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { FooterComponent } from './footer/footer.component';
import { OnboardingState } from './onboarding/state/onboarding.state';
import { ProfileState } from './profile/state/profile.state';
import { CookiesSettingsComponent } from './shared/core/cookies-settings/cookies-settings.component';
import { MainNavLanguagesComponent } from './shared/core/header/components/main-nav-languages/main-nav-languages.component';
import { HeaderModule } from './shared/core/header/header.module';
import { CustomReuseStrategy } from './shared/custom-reuse-strategy';
import { JWTTokenInterceptor } from './shared/interceptors/jwt-token.interceptor';
import { errorHandlerProvider } from './shared/providers/error-handler.provider';
import { sentryProviders } from './shared/providers/sentry.providers';
import { titleStrategyProvider } from './shared/providers/title-strategy.provider';
import { TRANSLOCO_OPTIONS } from './shared/providers/transloco.options';
import { AnalyticsService } from './shared/services/analytics.service';
import { AngularLocaleService } from './shared/services/angular-locale.service';
import { AppUpdateService } from './shared/services/app-update.service';
import { CanonicalLink } from './shared/services/canonical-link.service';
import { ChargebeeService } from './shared/services/chargebee.service';
import { CountriesService } from './shared/services/countries.service';
import { ElasticApmService } from './shared/services/elastic-apm.service';
import { ExploreLinkCreatorService } from './shared/services/explore-link-creator.service';
import { FrontendClientAnalyticsService } from './shared/services/frontend-client-analytics.service';
import { RouterLinkCreatorService } from './shared/services/router-link-creator.service';
import { ScriptService } from './shared/services/script.service';
import { ShareDialogsService } from './shared/services/share-dialogs.service';
import { AppState } from './shared/state/app.state';
import { HintUserWelcomeModule } from './widgets/dialogs/hint-user-welcome/hint-user-welcome.module';

import { LanguageModule } from '../../../shared/modules/language/language.module';
import { LanguageService } from '../../../shared/modules/language/services/language.service';
import { TranslateContentPipeModule } from '../../../shared/pipes/translate-content/translate-content-pipe.module';
import { environment } from '../environments/environment';

@NgModule({
  declarations: [AppComponent],
  imports: [
    // *** Angular modules ***
    BrowserAnimationsModule,
    CommonModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    LanguageModule,
    // *** APIs ***
    SmartHeadApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_base_path
    }),
    Auth0ApiModule.ApiModule.forRoot({
      rootUrl: environment.api_auth0
    }),
    AnalyticsApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_v2_base_path
    }),
    ClientStatisticsApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_v2_base_path
    }),
    LinkShortenerApiModule.ApiModule.forRoot({
      rootUrl: environment.api_link_shortener_base_path
    }),
    MailApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_v2_base_path
    }),
    PurchaseApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_v2_base_path
    }),
    UserRankApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_v2_base_path
    }),
    EsgApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_v2_base_path
    }),
    PdfGeneratorApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_v2_base_path
    }),
    AuditLogApiModule.ApiModule.forRoot({
      rootUrl: environment.api_smarthead_v2_base_path
    }),
    // *** 3th party ***
    NgxsModule.forRoot(
      [
        AppState,
        ProfileState,
        OnboardingState
      ],
      {
        developmentMode: !environment.production,
        selectorOptions: {
          // These Selector Settings are recommended in preparation for NGXS v4
          // see https://www.ngxs.io/advanced/options
          suppressErrors: false, // `true` by default
          injectContainerState: false
        }
      }
    ),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production
    }),
    MatMenuModule,
    MatSelectModule,
    MatSnackBarModule,
    AuthModule,
    NzModalModule,
    // *** Routing module ***
    AppRoutingModule,
    // *** SmartHead modules ***
    CookiesSettingsComponent,
    HeaderModule,
    HintUserWelcomeModule,
    TranslateContentPipeModule,
    FooterComponent,
    MainNavLanguagesComponent,
    UiPlatformNotificationComponent,
    TranslocoPipe
  ],
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: CustomReuseStrategy
    },
    errorHandlerProvider,
    provideTransloco(TRANSLOCO_OPTIONS),
    ...sentryProviders,
    titleStrategyProvider,
    provideAppInitializer(() => inject(LanguageService).initialize()),
    {
      provide: LOCALE_ID,
      deps: [AngularLocaleService],
      useFactory: (angularLocaleService: { locale: string }) => angularLocaleService.locale
    },
    // tbh, dunno why ANGULAR_LOCALE_SERVICE_TOKEN token does not work while in app.module, it's not in shared.module
    // {
    //   provide: ANGULAR_LOCALE_SERVICE_TOKEN,
    //   deps: [AngularLocaleService],
    //   useFactory: (angularLocaleService: AngularLocaleService) => angularLocaleService
    // },
    {
      provide: ANGULAR_LOCALE_SERVICE_TOKEN,
      deps: [AngularLocaleService],
      useFactory: (angularLocaleService: AngularLocaleService) => angularLocaleService
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JWTTokenInterceptor,
      multi: true
    }, // add JWT token to requests
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { hideRequiredMarker: true }
    },
    // 3th party pipes required by services
    LatinisePipe,
    SlugifyPipe,
    StripTagsPipe,
    // our services,
    AnalyticsService,
    CanonicalLink,
    ElasticApmService,
    FrontendClientAnalyticsService,
    ChargebeeService,
    CountriesService,
    ExploreLinkCreatorService,
    RouterLinkCreatorService,
    ScriptService,
    ShareDialogsService,
    AppUpdateService,
    provideHttpClient(withInterceptorsFromDi())
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
